import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Button,
  FormControlLabel,
  RadioGroup,
  Radio,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { every, isNumber, transform, values } from 'lodash-es';
import { Dialog } from '../../components/Dialog';
import { selectors as growerSelectors } from '../../store/dux/grower-dux';
import { actions } from '../../store/dux/yield-dux';
import { Table } from '../../components/table/Table';
import { NumericTextField } from '../../components/NumericTextField';
import { postCalibrate } from '../../lib/api';
import { useAlerts } from '../../hooks/use-alerts';
import { useContext } from './context.jsx';

export default function YieldCalibrationDialog(props) {
  const { selectedRawFields, onClose } = props;
  const { showError } = useAlerts();
  const dispatch = useDispatch();
  const growerId = useSelector(growerSelectors.selectedId) || '';
  const { year } = useContext();
  const [savingCalibration, setSavingCalibration] = useState(false);
  const [calibrationType, setCalibrationType] = useState('Dry');
  const [newFieldAverages, setFieldAverages] = useState(
    buildInitialCalibrationParams(),
  );

  function buildInitialCalibrationParams() {
    return transform(
      selectedRawFields,
      (fieldAverages, field) => {
        fieldAverages[field.aaid] = {
          aaid: field.aaid,
          fieldName: field.fieldName,
          crop: field.harvestedCrop,
          postCalibrationData: {},
        };
      },
      {},
    );
  }

  async function saveCalibration() {
    setSavingCalibration(true);

    postCalibrate(values(newFieldAverages), growerId, year)
      .then(() => dispatch(actions.loadYieldData(growerId, year)))
      .then(onClose)
      .catch(showError)
      .finally(() => setSavingCalibration(false));
  }

  function cancelEdit() {
    onClose();
  }

  function changeCalibrationType(type) {
    setFieldAverages(buildInitialCalibrationParams());
    setCalibrationType(type);
  }

  function handleChange(event, aaid) {
    const prevFieldAverage = newFieldAverages[aaid];

    setFieldAverages({
      ...newFieldAverages,
      [aaid]: {
        ...prevFieldAverage,
        postCalibrationData: {
          ...prevFieldAverage.postCalibrationData,
          [event.target.name]: event.target.value,
        },
      },
    });
  }

  function canRunPostCal() {
    return every(values(newFieldAverages), (row) => {
      const hasAvgBushel = isNumber(row.postCalibrationData?.avgBushel);
      const hasMoisture = isNumber(row.postCalibrationData?.moisture);

      return calibrationType === 'Dry'
        ? hasAvgBushel
        : hasAvgBushel && hasMoisture;
    });
  }

  return (
    <Dialog
      keepMounted
      open
      maxWidth="sm"
      fullWidth
      title="Post Harvest Calibration"
      hideFooter={false}
      onCancel={cancelEdit}
      actions={[
        <Button
          onClick={cancelEdit}
          data-testid={'cancel-edit'}
          variant="contained"
          color="dark"
        >
          Cancel
        </Button>,
        <LoadingButton
          disabled={!canRunPostCal()}
          onClick={saveCalibration}
          loading={savingCalibration}
          data-testid={'run-calibration'}
          variant="contained"
          color="primary"
        >
          Run
        </LoadingButton>,
      ]}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          height: '100%',
        }}
      >
        {buildRadioButtons()}
        <Box sx={{ flex: 1 }}>
          <Table
            autoHeight={true}
            pagination={true}
            getRowId={(row) => row.aaid}
            rows={values(newFieldAverages)}
            columns={buildColumns()}
          />
        </Box>
      </Box>
    </Dialog>
  );

  function buildRadioButtons() {
    return (
      <RadioGroup
        sx={{ mb: 2, ml: 1 }}
        row
        value={calibrationType}
        onChange={(event) => changeCalibrationType(event.target.value)}
      >
        <FormControlLabel
          sx={{ fontSize: 18, mr: 3 }}
          value="Dry"
          control={<Radio />}
          label="Dry Calibration"
        />
        <FormControlLabel
          sx={{ fontSize: 18 }}
          value="Wet"
          control={<Radio />}
          label="Wet Calibration"
        />
      </RadioGroup>
    );
  }

  function buildColumns() {
    return [
      {
        field: 'fieldName',
        headerName: 'Field Name',
        flex: 2,
      },
      {
        field: 'avgYield',
        headerName: `${calibrationType} Bushel Avg`,
        flex: 1,
        renderCell: ({ row }) => (
          <NumericTextField
            name="avgBushel"
            data-testid="yield-avg"
            placeholder="Required"
            variant="outlined"
            size="small"
            allowDecimals
            ignoreNonNumericInput
            value={row.postCalibrationData.avgBushel || ''}
            onChange={(event) => handleChange(event, row.aaid)}
          />
        ),
      },
      ...(calibrationType === 'Wet'
        ? [
            {
              field: 'moisture',
              headerName: 'Moisture',
              flex: 1,
              renderCell: ({ row }) => (
                <NumericTextField
                  name="moisture"
                  data-testid="yield-moisture"
                  placeholder="Required"
                  variant="outlined"
                  size="small"
                  allowDecimals
                  ignoreNonNumericInput
                  value={row.postCalibrationData.moisture || ''}
                  onChange={(event) => handleChange(event, row.aaid)}
                />
              ),
            },
          ]
        : []),
    ];
  }
}
